import React, { useEffect, useMemo } from 'react';
import {
  BaseInputProps,
  MultiSelectDropdown,
  useFormContextRequired,
} from 'ui-builder';
import { usePlansService, useTeamSetupAreasService } from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { get } from 'lodash';

interface SecondaryWorkflowsDropdownProps extends BaseInputProps<number[]> {
  projectTypeSource: string;
  primaryPlanSource: string;
  setupAreaIdSource: string;
}

export function SecondaryWorkflowsDropdown(props: SecondaryWorkflowsDropdownProps) {
  const planService = usePlansService();
  const setupAreasService = useTeamSetupAreasService();

  const formContext = useFormContextRequired();

  const projectType = get(formContext.data, props.projectTypeSource);
  const primaryPlanId = get(formContext.data, props.primaryPlanSource);
  const setupAreaId = get(formContext.data, props.setupAreaIdSource);

  const { data: plans } = useDataLoaderNew(
    async () => {
      if (projectType && setupAreaId) {
        return planService.getAllTeamPlans(projectType, setupAreaId);
      } else {
        return [];
      }
    },
    { dependencies: [projectType, setupAreaId] },
  );

  const { data: setupAreas } = useDataLoaderNew(setupAreasService.getTeamSetupAreas);

  const supportsSecondaryWorkflows = useMemo(() => {
    if (setupAreas && setupAreaId) {
      const area = setupAreas.find((a) => a.id === setupAreaId);
      if (!area) return false;
      return !area.hasSets;
    } else {
      return false;
    }
  }, [JSON.stringify(setupAreas), setupAreaId]);

  useEffect(() => {
    formContext.onChangeCallback?.({
      secondaryPlanIds: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryPlanId]);

  if (!projectType || !primaryPlanId || !supportsSecondaryWorkflows) {
    return null;
  }

  const options = (plans || [])
    .filter((plan) => plan.id !== primaryPlanId)
    .map((plan) => ({
      value: plan.id,
      label: plan.name,
    }));

  return (
    <MultiSelectDropdown<number>
      {...props}
      options={options}
    />
  );
}
